import { Info } from "lucide-react";

import { type AddressLight } from "shared/apiClient";
import { cn, getText, titleCase } from "shared/lib";

import {lotSizeDescription} from "@/lib/utils.ts";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components";
import { SiteOverviewMap } from "./SiteOverviewMap";

export interface SiteOverviewProps {
  address: AddressLight | undefined | null;
  className?: string;
}

export function SiteOverview({ address, className }: SiteOverviewProps) {
  const renderOverlays = () => {
    const overlays = address?.overlays || [];

    if (overlays.length > 0) {
      return (
        <ul className="text-bodymd list-outside list-disc pl-5">
          {/* @ts-expect-error TODO: swagger type for overlays is incorrect */}
          {overlays.map((overlay, index) => {
            return (
              <li key={index + "lot-overlay"}>
                {titleCase(overlay.description || "")}
              </li>
            );
          })}
        </ul>
      );
    } else {
      return (
        <p className="text-bodymd">
          {getText("🌱 There are no overlays associated with this address.")}
        </p>
      );
    }
  };

  return (
    <TooltipProvider>
      <div className={cn("flex flex-col gap-5", className)}>
        <SiteOverviewMap address={address} />

        {address?.unstructured_address ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-headingsm">{getText("Address")}</h3>

            <p className="text-bodymd">
              {titleCase(address.unstructured_address)}
            </p>
          </div>
        ) : null}

        {address?.lga_name ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-headingsm">{getText("Council")}</h3>

            <p className="text-bodymd capitalize">
              {address.lga_name.toLowerCase()}
            </p>
          </div>
        ) : null}

        {address?.lot_size_sqm ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-headingsm">{getText("Size")}</h3>

            <p className="text-bodymd">{lotSizeDescription(address)}</p>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <h3 className="text-headingsm">{getText("Zone")}</h3>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent>
                {getText(
                  "Zones regulate the use and development of land. Different zones typically represent different types of land use, such as residential or commercial.",
                )}
              </TooltipContent>
            </Tooltip>
          </div>

          <p className="text-bodymd">
            {address?.zones[0]?.description
              ? titleCase(address?.zones[0]?.description)
              : getText("🚧 No zone for this address was found.")}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <h3 className="text-headingsm">{getText("Overlays")}</h3>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent className="flex flex-col gap-1">
                <p>
                  {getText(
                    "An overlay is a layer of additional planning controls that is applied to a specific area.",
                  )}
                </p>
                <p>
                  {getText(
                    "Overlays are used to address specific planning issues or to protect certain features or values in an area.",
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {renderOverlays()}
        </div>
      </div>
    </TooltipProvider>
  );
}
